import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Position, useReactFlow } from '@xyflow/react';
import { AppNode, NodeType, NodeStatus, DetailPageData } from '@/types';
import { NodeTitle } from './parts/NodeTitle';
import { MagneticHandle } from './parts/MagneticHandle';
import { DetailPageContent } from './parts/content/DetailPageContent';
import { DetailPageAskPopover, askActionRegistry, DetailPageAskSnapshot } from './parts/content/DetailPageAskPopover';
import { InverseScaleBox } from './parts/InverseScaleBox';

const DETAIL_PAGE_WIDTH = 728;

interface DetailPageNodeProps {
  data: AppNode;
  selected: boolean;
  dragging?: boolean;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>) => void;
  onAddNode?: (type: NodeType, position?: { x: number; y: number }, initialData?: Record<string, unknown>) => string | undefined;
  /** 统一生成编排入口（EditorLayout handleNodeAction 下行）：分块出图创建节点后自动触发生图 */
  onNodeAction?: (id: string, prompt?: string) => void;
}

const DetailPageNodeComponent: React.FC<DetailPageNodeProps> = memo(({
  data,
  selected,
  dragging = false,
  onNodeUpdate,
  onAddNode,
  onNodeAction,
}) => {
  const isWorking = data.status === NodeStatus.WORKING;
  const detailData = data.data as DetailPageData;
  const planner = detailData.planner;

  // 高度自适应：节点高度由卡片内容决定（分屏提示词全量展示），不再使用固定阶段高度；
  // 实测高度仅回传 MagneticHandle（作 useLayoutEffect 依赖触发手柄屏幕坐标重缓存，保磁吸精度）
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [cardHeight, setCardHeight] = useState(0);
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const update = () => setCardHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // 节点底部询问弹窗（§4.5）：快照由 DetailPageContent 上行（仅 pendingAsk 引用变化时），
  // reply/cancel 经按节点 id 的 askActionRegistry 下行（渲染期注册最新闭包），弹窗渲染在卡片 overflow-hidden 之外
  const [askSnapshot, setAskSnapshot] = useState<DetailPageAskSnapshot | null>(null);
  const handlePendingAskChange = useCallback((snapshot: DetailPageAskSnapshot | null) => {
    setAskSnapshot(snapshot);
  }, []);

  // E9 zIndex 抬升：询问弹窗挂起期间抬升节点层级（React Flow 节点属性），
  // 防止外挂弹窗被下方相邻节点盖住，应答/取消后随弹窗收起回落
  const { updateNode: updateFlowNode } = useReactFlow();
  useEffect(() => {
    if (!askSnapshot) return;
    updateFlowNode(data.id, (node) => ({ ...node, zIndex: 1000 }));
    return () => {
      updateFlowNode(data.id, (node) => ({ ...node, zIndex: undefined }));
    };
  }, [askSnapshot, data.id, updateFlowNode]);

  // 手柄旁常驻指示语句：面板管理数量 + 手动连线接入数量实时合并（connected* 由连线派生自动写回）
  const productTotal =
    (planner?.productImageCount ?? 0) + (planner?.connectedProductImageNodeIds?.length ?? 0);
  const competitorTotal =
    (planner?.competitorImageCount ?? 0) + (planner?.connectedCompetitorImageNodeIds?.length ?? 0);
  const productIndicator = productTotal > 0 ? `产品图 · ${productTotal}张` : '产品图 · 未连接';
  const competitorIndicator = planner?.skipCompetitor
    ? '竞品图 · 已跳过'
    : competitorTotal > 0
      ? `竞品图 · ${competitorTotal}份`
      : '竞品图 · 未连接';

  return (
    <div
      className="relative"
      style={{ width: DETAIL_PAGE_WIDTH }}
      onDoubleClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.stopPropagation()}
    >
      {/* 节点标题 — 用 InverseScaleBox 包裹，画布缩放时保持视觉尺寸和位置不变
          zoom <= 0.7 时标题跟随画布缩放（字体变小），padding 也按比例缩小 */}
      <InverseScaleBox
        minZoom={0.5}
        className="absolute left-0 z-10"
        style={{ bottom: '100%', paddingBottom: '4px', width: '210px' }}
        scaleOrigin="bottom left"
      >
        <NodeTitle nodeId={data.id} title={data.title} type={data.type} onUpdate={onNodeUpdate} />
      </InverseScaleBox>

      <div
        ref={cardRef}
        data-node-card={data.id}
        className={`group relative overflow-hidden transition-[border-color,background-color] duration-300 rounded-[32px] border ${
          isWorking
            ? 'charge-border'
            : selected
              ? 'ring-2 ring-white/50 shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)] border-white/40'
              : 'border-white/25 hover:border-white/40'
        }`}
        style={{
          width: DETAIL_PAGE_WIDTH,
          background: selected ? 'rgba(38, 38, 38, 0.95)' : 'rgba(38, 38, 38, 0.85)',
          backdropFilter: 'blur(24px)',
          // 不能用 contain: paint — 与 backdrop-filter 冲突，导致缩放闪烁
          contain: 'layout style',
        }}
      >
        <div className="w-full relative">
          <DetailPageContent
            data={data}
            selected={selected}
            onNodeUpdate={onNodeUpdate}
            onAddNode={onAddNode}
            onNodeAction={onNodeAction}
            onPendingAskChange={handlePendingAskChange}
          />
        </div>

        {/* 详情页节点执行中不使用扫光遮罩（charge-overlay/scan-line）：保持内容清晰，仅以 charge-border 边框微光指示状态 */}
      </div>

      {/* 节点底部询问弹窗：渲染在卡片之外（根 div 无 overflow 裁剪），悬于节点下边缘 12px */}
      {askSnapshot && (
        <DetailPageAskPopover
          ask={askSnapshot.ask}
          kind={askSnapshot.kind}
          onReply={(reply, attachments, displayText) => askActionRegistry.get(data.id)?.reply(reply, attachments, displayText)}
          onCancel={() => askActionRegistry.get(data.id)?.cancel()}
        />
      )}

      <MagneticHandle
        type="target"
        position={Position.Left}
        nodeId={data.id}
        cardHeight={cardHeight}
        cardWidth={DETAIL_PAGE_WIDTH}
        category="product"
        offsetPercent={30}
        indicator={productIndicator}
      />

      <MagneticHandle
        type="target"
        position={Position.Left}
        nodeId={data.id}
        cardHeight={cardHeight}
        cardWidth={DETAIL_PAGE_WIDTH}
        category="competitor"
        offsetPercent={70}
        indicator={competitorIndicator}
      />

      <MagneticHandle
        type="source"
        position={Position.Right}
        nodeId={data.id}
        cardHeight={cardHeight}
        cardWidth={DETAIL_PAGE_WIDTH}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  const pd = prevProps.data?.data as DetailPageData;
  const nd = nextProps.data?.data as DetailPageData;

  return (
    prevProps.data?.id === nextProps.data?.id &&
    prevProps.selected === nextProps.selected &&
    prevProps.dragging === nextProps.dragging &&
    prevProps.data?.status === nextProps.data?.status &&
    prevProps.data?.title === nextProps.data?.title &&
    prevProps.data?.inputs?.length === nextProps.data?.inputs?.length &&
    pd?.mode === nd?.mode &&
    pd?.stage === nd?.stage &&
    pd?.generatedBlocks === nd?.generatedBlocks &&
    pd?.planner?.isPlannerComplete === nd?.planner?.isPlannerComplete &&
    pd?.planner?.architectureTable === nd?.planner?.architectureTable &&
    pd?.planner?.productImageCount === nd?.planner?.productImageCount &&
    pd?.planner?.productImageNodeIds?.length === nd?.planner?.productImageNodeIds?.length &&
    pd?.planner?.sellingPoints?.length === nd?.planner?.sellingPoints?.length &&
    pd?.planner?.sellingPointNodeIds?.length === nd?.planner?.sellingPointNodeIds?.length &&
    pd?.planner?.competitorImageCount === nd?.planner?.competitorImageCount &&
    pd?.planner?.competitorImageNodeIds?.length === nd?.planner?.competitorImageNodeIds?.length &&
    pd?.planner?.connectedProductImageNodeIds?.length === nd?.planner?.connectedProductImageNodeIds?.length &&
    pd?.planner?.connectedCompetitorImageNodeIds?.length === nd?.planner?.connectedCompetitorImageNodeIds?.length &&
    pd?.planner?.skipCompetitor === nd?.planner?.skipCompetitor &&
    pd?.planner?.style === nd?.planner?.style &&
    pd?.planner?.model === nd?.planner?.model &&
    pd?.planner?.imageModel === nd?.planner?.imageModel &&
    pd?.planner?.advancedOpen === nd?.planner?.advancedOpen &&
    pd?.planner?.copywritingResult === nd?.planner?.copywritingResult &&
    pd?.planner?.productAnalysis === nd?.planner?.productAnalysis &&
    pd?.planner?.competitorAnalysis === nd?.planner?.competitorAnalysis &&
    pd?.planner?.competitorStyleProfile === nd?.planner?.competitorStyleProfile &&
    pd?.planner?.competitorStyleBlacklist === nd?.planner?.competitorStyleBlacklist &&
    pd?.planner?.useCompetitorStyleReference === nd?.planner?.useCompetitorStyleReference &&
    pd?.planner?.competitorScreens === nd?.planner?.competitorScreens &&
    pd?.planner?.runState === nd?.planner?.runState &&
    pd?.designer?.productName === nd?.designer?.productName &&
    pd?.designer?.architectureSummary === nd?.designer?.architectureSummary &&
    pd?.designer?.style === nd?.designer?.style &&
    pd?.designer?.model === nd?.designer?.model &&
    pd?.designer?.hasUpstreamData === nd?.designer?.hasUpstreamData &&
    pd?.designer?.promptResult === nd?.designer?.promptResult &&
    pd?.designer?.splitPrompts === nd?.designer?.splitPrompts &&
    pd?.designer?.runState === nd?.designer?.runState
  );
});

export const DetailPageNode = DetailPageNodeComponent;
